import { selectBasketList, selectBasketSelectedWarehouses, BASKET_TYPES } from 'containers/OrderDetails/basketSlice'
import { estimatePrice, getDefaultDuration } from 'utils/shortTerm'
import { useEffect, useMemo, useState } from 'react'
import { useReduxSelector } from 'store'
import { selectConfig } from 'appSlice'
import { shopproducts } from 'rest/urls'
import { POST } from 'rest/request'

const type = process.env.REACT_APP_TYPE
const STORAGE_KEY = 'vanUpsellShown'

/** w tej sesji modal pokazujemy raz na pozycję koszyka (cartId) */
const shownInMemory = new Set<string>()

const wasShown = (cartId: string) => {
  if (shownInMemory.has(cartId)) return true
  try {
    return (JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]') as string[]).includes(cartId)
  } catch {
    return false
  }
}

export const markVanUpsellShown = (cartId: string) => {
  shownInMemory.add(cartId)
  try {
    const list = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]') as string[]
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...list.filter(i => i !== cartId), cartId]))
  } catch {
    // brak sessionStorage (tryb prywatny itp.) — zostaje pamięć
  }
}

export interface VanUpsellData {
  categoryId: string
  products: shop.RentalProduct[]
  /** auto do pokazania na zdjęciu: najpierw dostępne teraz, potem pierwsze z listy */
  featured: shop.RentalProduct
  photo?: string
  /** najniższa cena „od" spośród aut (jeśli shopproducts zwraca shortTerm.grid) */
  priceFrom?: { gross: number, minutes: number }
}

/**
 * ldz01: upsell busa przed koszykiem („a może frytki do tego?”).
 * Pobiera auta ze sklepiku (shopproducts, source: rental) dla skrytki z bieżącej pozycji koszyka,
 * żeby modal pokazywał się tylko gdy auto faktycznie jest — i żeby mieć jego zdjęcie.
 */
const useVanUpsell = (cartId?: string) => {
  const config = useReduxSelector(selectConfig)
  const list = useReduxSelector(selectBasketList) as basic.Form[]
  const selectedWarehouses = useReduxSelector(selectBasketSelectedWarehouses) as basic.Form[]
  const [data, setData] = useState<VanUpsellData | null>(null)

  const selectedWarehouse = cartId ? selectedWarehouses.find(w => w.cartId === cartId) : undefined
  const hasVanInBasket = list.some(l => l.type === BASKET_TYPES.rent && l.rentalUpsellFor)
  const isEnabled = type === 'ldz01' && !!config?.rentalUpsellEnabled && !!cartId && !!selectedWarehouse

  const related = useMemo(() => {
    if (!selectedWarehouse) return undefined
    return selectedWarehouse.id
      ? { object: 'Warehouse', id: selectedWarehouse.id }
      : { object: 'Warehouse', hash: selectedWarehouse.hash, placeId: selectedWarehouse.placeId }
  }, [selectedWarehouse])

  useEffect(() => {
    if (!isEnabled || !related) {
      setData(null)
      return
    }
    let cancelled = false
    POST(shopproducts, { body: { related, source: 'rental' } })
      .then((response: shop.ProductsCategory[]) => {
        if (cancelled || !Array.isArray(response)) return
        const category = response.find(c => c.type === 'rental' || c.products?.some(p => (p as shop.RentalProduct).type === 'rental'))
        const products = (category?.products || []).filter((p): p is shop.RentalProduct => (p as shop.RentalProduct).type === 'rental')
        if (!category || products.length === 0) {
          setData(null)
          return
        }
        const featured = products.find(p => p.availability?.availableNow && (p.warehousePhoto || p.vehicle?.photo))
          || products.find(p => p.warehousePhoto || p.vehicle?.photo)
          || products[0]

        const priceFrom = products.reduce<VanUpsellData['priceFrom']>((best, p) => {
          if (!p.shortTerm) return best
          const minutes = getDefaultDuration(p.shortTerm)
          const estimate = minutes ? estimatePrice(p.shortTerm, minutes) : undefined
          if (!estimate || !minutes) return best
          return !best || estimate.gross < best.gross ? { gross: estimate.gross, minutes } : best
        }, undefined)

        setData({
          categoryId: category.id,
          products,
          featured,
          photo: featured.warehousePhoto || featured.vehicle?.photo,
          priceFrom
        })
      })
      .catch(() => {
        if (!cancelled) setData(null)
      })
    return () => {
      cancelled = true
    }
  }, [isEnabled, related])

  return {
    data,
    /** czy przy „Dalej” zamiast koszyka pokazać modal */
    shouldShow: !!data && !!cartId && !hasVanInBasket && !wasShown(cartId)
  }
}

export default useVanUpsell
